import React from 'react';
import { Box, Button } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '../../../contexts/authContext';
import { MDTypography } from '../../../components/MDTypography';
import { Bill } from '../ClaimDetailsTypes';
import {
  canUserApproveReject,
  isVerdictActionable,
  getVerdictColor,
  getVerdictDisplayText,
} from '../utils/billActionUtils';
import useBillActions from '../hooks/useBillActions';
import ApprovalModal from '../ApprovalModal';
import RejectionModal from '../RejectionModal';
import VerdictEditModal from './VerdictEditModal';
import { BillVerdictStatus } from '../../../types';

const useStyles = makeStyles((theme) => ({
  actionButton: {
    minWidth: 80,
    padding: theme.spacing(0.5, 1),
    fontSize: '0.75rem',
    margin: theme.spacing(0, 0.5),
  },
  approveButton: {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.success.dark,
    },
  },
  rejectButton: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.error.dark,
    },
  },
  clickableVerdict: {
    cursor: 'pointer',
    textDecoration: 'underline',
    '&:hover': {
      opacity: 0.8,
    },
  },
  unClickableVerdict: {
    cursor: 'default',
    opacity: 0.6,
    '&:hover': {
      opacity: 0.6,
    },
  },
  verdictContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
}));

interface BillActionCellProps {
  bill: Bill;
  claimId: string;
  loading?: boolean;
  displayMode?: 'buttons' | 'text';
  onVerdictUpdate: (
    billId: number,
    verdict: BillVerdictStatus,
    amount: number,
    reason: string
  ) => void;
}

export const BillActionCell: React.FC<BillActionCellProps> = ({
  bill,
  claimId,
  loading = false,
  displayMode = 'buttons',
  onVerdictUpdate,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { user } = useAuthContext();

  const billActions = useBillActions({ onVerdictUpdate, loading });

  // Check if user can perform approve/reject actions
  const canApproveReject = canUserApproveReject(user?.role);
  const isActionable = isVerdictActionable(bill);

  if (!canApproveReject) {
    // For users who can't approve/reject, just show the verdict status
    const verdict = bill.verdict?.verdict || 'Pending';
    return (
      <MDTypography variant="caption" color={getVerdictColor(verdict)}>
        {getVerdictDisplayText(verdict, bill.verdict?.amount)}
      </MDTypography>
    );
  }

  if (isActionable) {
    // Show approve/reject buttons for bills without verdicts
    return (
      <>
        <Box display="flex">
          <Button
            className={`${classes.actionButton} ${classes.approveButton}`}
            onClick={() => billActions.handleApproveClick(bill)}
            disabled={loading}
            size="small"
          >
            {t('Approve')}
          </Button>
          <Button
            className={`${classes.actionButton} ${classes.rejectButton}`}
            onClick={() => billActions.handleRejectClick(bill)}
            disabled={loading}
            size="small"
          >
            {t('Reject')}
          </Button>
        </Box>

        {/* Modals for new approvals/rejections */}
        <ApprovalModal
          open={billActions.approvalModalOpen}
          bill={billActions.selectedBill}
          claimId={claimId}
          loading={loading}
          onClose={billActions.handleCloseApprovalModal}
          onApprove={billActions.handleApprove}
        />

        <RejectionModal
          open={billActions.rejectionModalOpen}
          bill={billActions.selectedBill}
          claimId={claimId}
          loading={loading}
          onClose={billActions.handleCloseRejectionModal}
          onReject={billActions.handleReject}
        />
      </>
    );
  } else {
    // Show clickable verdict for bills with existing verdicts
    const verdict = bill.verdict?.verdict || 'Pending';
    const verdictColor = getVerdictColor(verdict);
    const clickable = verdict !== 'paid' && verdict !== 'processing' && verdict !== 'processed';

    return (
      <>
        <Box className={classes.verdictContainer}>
          <MDTypography
            variant="caption"
            color={verdictColor}
            className={
              clickable ? classes.clickableVerdict : classes.unClickableVerdict
            }
            onClick={() =>
              clickable ? billActions.handleVerdictClick(bill) : undefined
            }
          >
            {getVerdictDisplayText(verdict, bill.verdict?.amount)}{' '}
            {clickable && (
              <>
                <span style={{ color: 'black', textDecoration: 'underline' }}>
                  {'(Edit)'}
                </span>
                <span style={{ color: 'black' }}> &#x270E;</span>
              </>
            )}
          </MDTypography>
          {bill.verdict?.reason && displayMode === 'text' && (
            <MDTypography variant="caption" color="secondary">
              {bill.verdict.reason}
            </MDTypography>
          )}
        </Box>

        {/* Modal for editing existing verdicts */}
        <VerdictEditModal
          open={billActions.verdictEditModalOpen}
          bill={billActions.selectedBill}
          loading={loading}
          onClose={billActions.handleCloseVerdictEditModal}
          onSave={billActions.handleVerdictEdit}
        />
      </>
    );
  }
};

export default BillActionCell;
